import { Dialog } from '@rio-cloud/rio-uikit';
import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { updateUptimeAsset } from '../../../api/fetchAssets';

interface IEditOrdometerHourmeterDialogProps {
    type: 'odometer' | 'hourmeter';
    assetId: string;
    onClose: (assetId: string) => void;
}

export const EditOrdometerHourmeterDialog = ({ type, assetId, onClose }: IEditOrdometerHourmeterDialogProps) => {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState<number | undefined>();
    const [loading, setLoading] = useState(false);
    const intl = useIntl();
    const inputId = useId();
    const isOdometer = type === 'odometer';
    const title = intl.formatMessage({
        id: isOdometer ? 'intl-msg:monitoring.edit.updateOdometer' : 'intl-msg:monitoring.edit.updateHourmeter',
    });

    const handleOpen = () => {
        setValue(undefined);
        setLoading(false);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setValue(undefined);
        setLoading(false);
    };

    const handleSave = async () => {
        if (!value) {
            return;
        }
        setLoading(true);
        try {
            const response = await updateUptimeAsset({
                assetId,
                [type]: value,
                [isOdometer ? 'lastOdometerUpdate' : 'lastHourmeterUpdate']: new Date(),
            });
            if (!response) {
                throw new Error('Asset metric update failed');
            }
            handleClose();
            onClose(assetId);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    return (
        <>
            <button type='button' className='fm-kpi__edit' title={title} aria-label={title} onClick={handleOpen}>
                <span className='rioglyph rioglyph-pencil-square text-size-18' />
            </button>

            <Dialog
                show={open}
                onClose={handleClose}
                title={title}
                subtitle={intl.formatMessage({ id: 'intl-msg:monitoring.edit.subtitle' })}
                bsSize='xs'
                footer={
                    <>
                        <button
                            type='button'
                            onClick={handleSave}
                            className={`btn btn-primary ${loading ? 'btn-loading' : ''} margin-right-10`}
                            disabled={!value || loading}
                        >
                            {intl.formatMessage({ id: 'intl-msg:monitoring.edit.updateButton' })}
                        </button>
                        <button type='button' onClick={handleClose} className='btn btn-default'>
                            {intl.formatMessage({ id: 'intl-msg:monitoring.edit.closeButton' })}
                        </button>
                    </>
                }
                body={
                    <div className='w-full mx-auto bg-white dark:bg-neutral-900 rounded-lg p-6'>
                        <div className='form-group'>
                            <label htmlFor={inputId}>
                                {intl.formatMessage({
                                    id: isOdometer
                                        ? 'intl-msg:monitoring.edit.odometerLabel'
                                        : 'intl-msg:monitoring.edit.hourmeterLabel',
                                })}
                            </label>
                            <input
                                id={inputId}
                                className='form-control'
                                type='number'
                                placeholder={intl.formatMessage({ id: 'intl-msg:monitoring.edit.valuePlaceholder' })}
                                onChange={event => {
                                    const digits = event.target.value.replace(/\D/g, '');
                                    event.target.value = digits;
                                    setValue(Number.parseInt(digits || '0'));
                                }}
                            />
                        </div>
                    </div>
                }
            />
        </>
    );
};
